import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { collection, doc, getDocs, writeBatch } from "firebase/firestore";
import Sidebar from "./sidebar";
import { db } from "../firebase";

const COLLECTIONS = [
  { name: "employee", label: "Active" },
  { name: "archived", label: "Archived" },
];

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const recordKey = (record) => `${record.collection}:${record.id}`;

const fullName = (record) =>
  `${record.data.firstName || ""} ${record.data.surName || ""}`.trim();

const SYSTEM_FIELDS = new Set([
  "conc",
  "files",
  "doc",
  "id",
  "uid",
  "createdAt",
  "updatedAt",
  "dateCreated",
  "sourceCollection",
  "sourceDocId",
  "deletedAt",
  "dedupKey",
  "reason",
  "mergedInto",
  "mergedAt",
  "mergeHistory",
  "mergedFileCount",
  "mergedCorrespondenceCount",
  "mergedFieldNames",
]);

const isBlank = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

const fileKey = (file) => {
  if (!file || typeof file !== "object") return "";
  return String(file.url || file.path || file.name || "").trim();
};

const concKey = (entry) => {
  if (!entry || typeof entry !== "object") return "";
  if (entry.id) return `id:${String(entry.id)}`;
  const key = ["concDate", "concType", "concLog", "concComment", "concImage"]
    .map((field) => normalize(entry[field]))
    .join("|");
  return key === "||||" ? "" : key;
};

const uniqueByKey = (items, getKey) => {
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(item);
  });
  return result;
};

const arrayLength = (value) => (Array.isArray(value) ? value.length : 0);

const latestConcDate = (record) => {
  const dates = (Array.isArray(record.data.conc) ? record.data.conc : [])
    .map((entry) => normalize(entry && entry.concDate))
    .filter(Boolean)
    .sort();
  return dates.length > 0 ? dates[dates.length - 1] : "";
};

const groupRecords = (records, matchMode) => {
  const map = new Map();

  records.forEach((record) => {
    const firstName = normalize(record.data.firstName);
    const surName = normalize(record.data.surName);
    if (!firstName && !surName) return;

    const nameKey = `${firstName}|${surName}`;
    const key =
      matchMode === "nameDob"
        ? `${nameKey}|${normalize(record.data.DOB)}`
        : nameKey;

    if (!map.has(key)) map.set(key, []);
    map.get(key).push(record);
  });

  const collectionOrder = { employee: 0, archived: 1 };

  return Array.from(map.entries())
    .filter(([, group]) => group.length > 1)
    .map(([key, group]) => {
      const sorted = group.slice().sort((a, b) => {
        const collectionDiff =
          (collectionOrder[a.collection] ?? 99) -
          (collectionOrder[b.collection] ?? 99);
        if (collectionDiff !== 0) return collectionDiff;
        return String(a.id).localeCompare(String(b.id));
      });
      return {
        key,
        name: fullName(sorted[0]) || "Unnamed record",
        records: sorted,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

const buildMergePlan = (keepRecord, toRemove) => {
  const keep = keepRecord.data;
  const keepFiles = Array.isArray(keep.files) ? keep.files : [];
  const keepConc = Array.isArray(keep.conc) ? keep.conc : [];

  const files = uniqueByKey(
    [
      ...keepFiles,
      ...toRemove.flatMap((record) =>
        Array.isArray(record.data.files) ? record.data.files : []
      ),
    ],
    fileKey
  );
  const conc = uniqueByKey(
    [
      ...keepConc,
      ...toRemove.flatMap((record) =>
        Array.isArray(record.data.conc) ? record.data.conc : []
      ),
    ],
    concKey
  );

  const seenFileKeys = new Set(keepFiles.map(fileKey).filter(Boolean));
  const seenConcKeys = new Set(keepConc.map(concKey).filter(Boolean));
  const fields = {};
  const perRecord = new Map();
  let filesAdded = 0;
  let concAdded = 0;

  toRemove.forEach((record) => {
    const counts = { files: 0, conc: 0, fields: [] };
    perRecord.set(recordKey(record), counts);

    (Array.isArray(record.data.files) ? record.data.files : []).forEach(
      (file) => {
        const key = fileKey(file);
        if (!key || seenFileKeys.has(key)) return;
        seenFileKeys.add(key);
        counts.files += 1;
        filesAdded += 1;
      }
    );

    (Array.isArray(record.data.conc) ? record.data.conc : []).forEach(
      (entry) => {
        const key = concKey(entry);
        if (!key || seenConcKeys.has(key)) return;
        seenConcKeys.add(key);
        counts.conc += 1;
        concAdded += 1;
      }
    );

    Object.entries(record.data).forEach(([field, value]) => {
      if (SYSTEM_FIELDS.has(field) || field in fields) return;
      if (Array.isArray(value) || (value && typeof value === "object")) return;
      if (isBlank(value) || !isBlank(keep[field])) return;
      fields[field] = value;
      counts.fields.push(field);
    });
  });

  const updates = {};
  if (files.length > 0 || keep.files !== undefined) updates.files = files;
  if (conc.length > 0 || keep.conc !== undefined) updates.conc = conc;
  Object.assign(updates, fields);

  return {
    perRecord,
    filesAdded,
    concAdded,
    fieldsAdded: Object.keys(fields).length,
    updates,
  };
};

export default function DuplicateRecords() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState("");
  const [matchMode, setMatchMode] = useState("name");
  const [keepSelections, setKeepSelections] = useState({});

  const groups = useMemo(
    () => groupRecords(records, matchMode),
    [records, matchMode]
  );

  useEffect(() => {
    const next = { ...keepSelections };
    groups.forEach((group) => {
      const valid = group.records.some(
        (record) => recordKey(record) === next[group.key]
      );
      if (!valid) next[group.key] = recordKey(group.records[0]);
    });
    setKeepSelections(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const [employeeSnap, archivedSnap] = await Promise.all(
        COLLECTIONS.map((entry) =>
          getDocs(collection(db, entry.name)).then((snapshot) => ({
            ...entry,
            snapshot,
          }))
        )
      );

      const list = [];
      employeeSnap.snapshot.forEach((document) => {
        list.push({
          collection: employeeSnap.name,
          collectionLabel: employeeSnap.label,
          id: document.id,
          data: document.data(),
        });
      });
      archivedSnap.snapshot.forEach((document) => {
        list.push({
          collection: archivedSnap.name,
          collectionLabel: archivedSnap.label,
          id: document.id,
          data: document.data(),
        });
      });

      setRecords(list);
    } catch (error) {
      alert("Could not load duplicate records: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const setKeep = (groupKey, key) => {
    setKeepSelections((current) => ({ ...current, [groupKey]: key }));
  };

  const mergeDuplicates = async (group) => {
    const keepKey = keepSelections[group.key];
    const keepRecord = group.records.find(
      (record) => recordKey(record) === keepKey
    );
    const toRemove = group.records.filter(
      (record) => recordKey(record) !== keepKey
    );

    if (!keepRecord || toRemove.length === 0) return;

    if (toRemove.length > 200) {
      alert(
        "This group has too many records to merge in one action. Please split it up and try again."
      );
      return;
    }

    const plan = buildMergePlan(keepRecord, toRemove);
    const timestamp = new Date().toISOString();
    const fieldNames = Object.keys(plan.updates).filter(
      (field) => field !== "files" && field !== "conc"
    );
    const fieldPreview =
      fieldNames.slice(0, 8).join(", ") +
      (fieldNames.length > 8 ? ", ..." : "");

    const keepDob = normalize(keepRecord.data.DOB);
    const otherDobs = toRemove
      .map((record) => normalize(record.data.DOB))
      .filter(Boolean);
    const dobMismatch =
      !!keepDob && otherDobs.some((dob) => dob !== keepDob);
    const missingDob =
      !keepDob || toRemove.some((record) => !normalize(record.data.DOB));
    const archivedWarning =
      keepRecord.collection === "archived" &&
      group.records.some((record) => record.collection === "employee");

    const lines = [
      `Keep: ${fullName(keepRecord)} (${keepRecord.collectionLabel}, ID ${
        keepRecord.id
      })`,
      "",
      `Merge ${toRemove.length} duplicate record(s) into it, then remove them:`,
      `- ${plan.filesAdded} new file(s)`,
      `- ${plan.concAdded} new correspondence entr${
        plan.concAdded === 1 ? "y" : "ies"
      }`,
    ];

    if (plan.fieldsAdded > 0) {
      lines.push(`- Fill ${plan.fieldsAdded} blank field(s): ${fieldPreview}`);
    } else {
      lines.push("- No blank fields to fill");
    }

    lines.push(
      "",
      'Removed records will be moved to "deleted" with a link to the kept record.'
    );

    if (archivedWarning) {
      lines.push(
        "",
        "WARNING: You selected an archived record. The merged record will stay archived."
      );
    }
    if (dobMismatch) {
      lines.push(
        "",
        "WARNING: The dates of birth do not match. You may be merging different people."
      );
    }
    if (missingDob) {
      lines.push(
        "",
        "WARNING: One or more records have no date of birth. Check carefully before merging."
      );
    }

    if (!window.confirm(lines.join("\n"))) return;

    setMerging(group.key);
    try {
      const batch = writeBatch(db);
      const updates = {
        ...plan.updates,
        mergeHistory: [
          ...(Array.isArray(keepRecord.data.mergeHistory)
            ? keepRecord.data.mergeHistory
            : []),
          ...toRemove.map((record) => {
            const counts = plan.perRecord.get(recordKey(record)) || {
              files: 0,
              conc: 0,
              fields: [],
            };
            return {
              collection: record.collection,
              id: record.id,
              mergedAt: timestamp,
              filesAdded: counts.files,
              concAdded: counts.conc,
              fieldsFilled: counts.fields,
            };
          }),
        ],
      };

      batch.update(doc(db, keepRecord.collection, keepRecord.id), updates);

      toRemove.forEach((record) => {
        const counts = plan.perRecord.get(recordKey(record)) || {
          files: 0,
          conc: 0,
          fields: [],
        };
        const deletedRef = doc(collection(db, "deleted"));
        batch.set(deletedRef, {
          ...record.data,
          sourceCollection: record.collection,
          sourceDocId: record.id,
          deletedAt: timestamp,
          dedupKey: group.key,
          reason: "Duplicate merge",
          mergedInto: {
            collection: keepRecord.collection,
            id: keepRecord.id,
          },
          mergedAt: timestamp,
          mergedFileCount: counts.files,
          mergedCorrespondenceCount: counts.conc,
          mergedFieldNames: counts.fields,
        });
        batch.delete(doc(db, record.collection, record.id));
      });

      await batch.commit();
      await loadRecords();
    } catch (error) {
      alert("Could not merge all duplicates: " + error.message);
      await loadRecords();
    } finally {
      setMerging("");
    }
  };

  const totalDuplicateRecords = groups.reduce(
    (sum, group) => sum + group.records.length,
    0
  );

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3 }}
        style={{ marginTop: "50px" }}
      >
        <div style={{ width: "100%" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Duplicate Records
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            Records grouped by matching names. Choose which record to keep,
            then merge the files and correspondence from the others into it
            before removing them. Removed records are moved to the "deleted"
            collection with a link to the kept record.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              select
              size="small"
              label="Match duplicates by"
              value={matchMode}
              onChange={(e) => setMatchMode(e.target.value)}
              style={{ width: 300 }}
            >
              <MenuItem value="name">First and last name</MenuItem>
              <MenuItem value="nameDob">
                First name, last name and date of birth
              </MenuItem>
            </TextField>
            <Typography>
              {groups.length} duplicate group
              {groups.length === 1 ? "" : "s"} / {totalDuplicateRecords} record
              {totalDuplicateRecords === 1 ? "" : "s"}
            </Typography>
            <Button
              variant="outlined"
              onClick={loadRecords}
              disabled={loading || !!merging}
            >
              Refresh
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>Loading records...</Typography>
            </Box>
          ) : groups.length === 0 ? (
            <Paper sx={{ p: 3 }}>
              <Typography>No duplicate records found.</Typography>
            </Paper>
          ) : (
            groups.map((group) => {
              const keepKey = keepSelections[group.key];
              const remaining = group.records.length - 1;
              return (
                <Paper key={group.key} sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">{group.name}</Typography>
                    <Button
                      variant="contained"
                      color="error"
                      disabled={!!merging}
                      onClick={() => mergeDuplicates(group)}
                    >
                      {merging === group.key
                        ? "Merging..."
                        : `Merge & remove ${remaining} duplicate${
                            remaining === 1 ? "" : "s"
                          }`}
                    </Button>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Keep</TableCell>
                          <TableCell>Collection</TableCell>
                          <TableCell>First Name</TableCell>
                          <TableCell>Surname</TableCell>
                          <TableCell>Date of Birth</TableCell>
                          <TableCell>Postcode</TableCell>
                          <TableCell>Gender</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>Files</TableCell>
                          <TableCell>Correspondence</TableCell>
                          <TableCell>Latest Correspondence Date</TableCell>
                          <TableCell>Firestore ID</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.records.map((record) => {
                          const key = recordKey(record);
                          return (
                            <TableRow
                              key={key}
                              hover
                              selected={keepKey === key}
                              onClick={() => setKeep(group.key, key)}
                              style={{ cursor: "pointer" }}
                            >
                              <TableCell padding="checkbox">
                                <Radio
                                  checked={keepKey === key}
                                  onChange={() => setKeep(group.key, key)}
                                  inputProps={{
                                    "aria-label": `Keep ${fullName(record)}`,
                                  }}
                                />
                              </TableCell>
                              <TableCell>{record.collectionLabel}</TableCell>
                              <TableCell>{record.data.firstName || ""}</TableCell>
                              <TableCell>{record.data.surName || ""}</TableCell>
                              <TableCell>{record.data.DOB || ""}</TableCell>
                              <TableCell>{record.data.postCode || ""}</TableCell>
                              <TableCell>{record.data.gender || ""}</TableCell>
                              <TableCell>{record.data.startDate || ""}</TableCell>
                              <TableCell>{arrayLength(record.data.files)}</TableCell>
                              <TableCell>{arrayLength(record.data.conc)}</TableCell>
                              <TableCell>{latestConcDate(record)}</TableCell>
                              <TableCell>{record.id}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              );
            })
          )}
        </div>
      </Box>
    </Box>
  );
}
